import React from "react";
import {SaveTemplateField} from "./SaveTemplateField.tsx";
import Switch from "../Switch.tsx";
import {CreditEstimateLine} from "../Credits/CreditEstimateLine.tsx";
import type {ICreditBreakdown} from "../../utils/creditsBreakdown.ts";

interface SubtitleFinalSetupProps {
  estimatedCredits: number;
  /** `Infinity` for an unlimited workspace. */
  availableCredits: number;
  /** The quote behind `estimatedCredits`, row by row; the estimate line prints its `quote.total`. */
  breakdown: ICreditBreakdown;
  canSaveTemplate: boolean;
  shouldSaveTemplate: boolean;
  templateName: string;
  setShouldSaveTemplate: (shouldSave: boolean) => void;
  setTemplateName: (name: string) => void;
}

export const SubtitleFinalSetup: React.FC<SubtitleFinalSetupProps> = (
  {
    estimatedCredits,
    availableCredits,
    breakdown,
    canSaveTemplate,
    shouldSaveTemplate,
    templateName,
    setShouldSaveTemplate,
    setTemplateName,
  }: SubtitleFinalSetupProps
) => {
  const isOverBudget: boolean = estimatedCredits > availableCredits;
  const availabilityCopy: string = availableCredits === Infinity
    ? "Unlimited credits"
    : isOverBudget
      ? `Only ${availableCredits} credits available`
      : `${availableCredits} credits available`;

  return (
    <div
      data-testid="subtitle-final-setup"
      className="shrink-0 border-t border-n-500 pt-4"
    >
      <div
        data-testid="subtitle-step-cost"
        data-over-budget={String(isOverBudget)}
        className="flex items-center justify-between gap-4"
      >
        <div className="min-w-0">
          {/* The dialog panel clips overflow, so the breakdown opens upward over the step's scroll area. */}
          <CreditEstimateLine breakdown={breakdown} isOverBudget={isOverBudget} placement="top"/>
          <p className={`mt-0.5 text-xs ${isOverBudget ? "text-red-500" : "text-n-400"}`}>
            {availabilityCopy}
          </p>
        </div>
        <div className="flex min-h-10 shrink-0 items-center gap-3">
          <span className="text-sm font-semibold text-n-300">Save as template</span>
          <Switch
            ariaLabel="Save as template"
            value={shouldSaveTemplate}
            setValue={setShouldSaveTemplate}
            disabled={!canSaveTemplate}
          />
        </div>
      </div>
      {shouldSaveTemplate && (
        <div id="subtitle-template-name" className="mt-3">
          <SaveTemplateField
            compact
            canSave={canSaveTemplate}
            value={templateName}
            onChange={setTemplateName}
          />
        </div>
      )}
    </div>
  );
};
